// src/layouts/BottomDrawer/tabs/LeadsTab/index.jsx
// *****************************************************************************
// LEADS TAB
// *****************************************************************************

// *****************************************************************************
// IMPORTS
// *****************************************************************************

// 1. React
import { useCallback, useEffect, useRef, useState } from 'react';

// 2. External libraries
import { Button, Card, Empty, Flex, Modal, Spin, Typography } from 'antd';
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  StarFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';

// 3. Services
import axiosService from 'services/axiosService';
import appService from 'services/appService';

// 4. Utils
import translateX from 'utils/translateX';

// 5. Constants
import { API } from 'constants/Api';

// *****************************************************************************
// SETUP
// *****************************************************************************
dayjs.extend(relativeTime);

// *****************************************************************************
// COMPONENT
// *****************************************************************************

const LeadsTab = props => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { onReady } = props;

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const initialLoadDone = useRef(false);

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  // (none)

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [confirmLead, setConfirmLead] = useState(null);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    fetchLeads().finally(() => {
      onReady?.();
    });
  }, []);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const result = await axiosService.get(API.LEADS.MARKETPLACE);
      setLeads(Array.isArray(result) ? result : result?.data || []);
    } catch {
      // ignore fetch errors silently
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfirm = useCallback(lead => {
    setConfirmLead(lead);
  }, []);

  const handleCancelConfirm = useCallback(() => {
    setConfirmLead(null);
  }, []);

  const handlePurchase = useCallback(async () => {
    if (!confirmLead) return;

    const lead = confirmLead;
    setConfirmLead(null);
    setPurchasing(lead._id);

    try {
      const url = API.LEADS.PURCHASE.replace(':_id', lead._id);
      const result = await axiosService.put(url);

      if (result) {
        setLeads(prev => prev.filter(l => l._id !== lead._id));
        appService.notification({
          flag: 'success',
          title: translateX('lead_purchased_success'),
        });
      }
    } catch {
      // error handled by axiosService interceptors
    } finally {
      setPurchasing(null);
    }
  }, [confirmLead]);

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  // (none)

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <Empty
        description={translateX('no_leads_available')}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: '32px 0' }}
      />
    );
  }

  return (
    <>
      {leads.map(lead => (
        <LeadCard
          key={lead._id}
          lead={lead}
          purchasing={purchasing === lead._id}
          onConfirm={handleConfirm}
        />
      ))}

      <Modal
        open={!!confirmLead}
        title={translateX('confirm_purchase')}
        okText={translateX('buy_lead')}
        cancelText={translateX('cancel')}
        onOk={handlePurchase}
        onCancel={handleCancelConfirm}
        confirmLoading={!!purchasing}
      >
        {confirmLead && (
          <Flex vertical gap={8} style={{ padding: '8px 0' }}>
            <Typography.Text>
              <strong>{confirmLead.maskedName || confirmLead.name}</strong>
            </Typography.Text>

            {confirmLead.interest && (
              <Flex align="center" gap={6}>
                <ShopOutlined />
                <Typography.Text type="secondary">{confirmLead.interest}</Typography.Text>
              </Flex>
            )}

            <Flex align="center" gap={6}>
              <StarFilled style={{ color: '#722ed1' }} />
              <Typography.Text>
                {translateX('lead_cost')}:{' '}
                <strong>
                  {confirmLead.sparkCoinsCost ?? confirmLead.cost ?? 0} SparkCoins
                </strong>
              </Typography.Text>
            </Flex>
          </Flex>
        )}
      </Modal>
    </>
  );
};

// *****************************************************************************
// EXPORT
// *****************************************************************************

export default LeadsTab;

// *****************************************************************************
// HELPERS
// *****************************************************************************

function LeadCard({ lead, purchasing, onConfirm }) {
  const cost = lead.sparkCoinsCost ?? lead.cost ?? 0;
  const createdAt = lead.createdAt ? dayjs(lead.createdAt).fromNow() : null;
  const maskedName = lead.maskedName || lead.name || '—';

  return (
    <Card size="small" style={{ marginBottom: 8 }}>
      <Flex justify="space-between" align="flex-start" gap={12}>
        <Flex vertical gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            {maskedName}
          </Typography.Text>

          {lead.interest && (
            <Flex align="center" gap={6}>
              <ShopOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {lead.interest}
              </Typography.Text>
            </Flex>
          )}

          {lead.landingPage && (
            <Flex align="center" gap={6}>
              <EnvironmentOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {lead.landingPage}
              </Typography.Text>
            </Flex>
          )}

          {createdAt && (
            <Flex align="center" gap={6}>
              <ClockCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }} />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {createdAt}
              </Typography.Text>
            </Flex>
          )}
        </Flex>

        <Flex vertical align="flex-end" gap={8} style={{ flexShrink: 0 }}>
          <Flex align="center" gap={4}>
            <StarFilled style={{ color: '#722ed1', fontSize: 13 }} />
            <Typography.Text strong style={{ fontSize: 13, color: '#722ed1' }}>
              {cost}
            </Typography.Text>
          </Flex>

          <Button
            type="primary"
            size="small"
            loading={purchasing}
            onClick={() => onConfirm(lead)}
          >
            {translateX('buy_lead')}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}
